import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

// Generate a simple session ID for tracking
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

export const useAnalytics = () => {
  const location = useLocation();

  const trackEvent = async (eventType: string, metadata: any = {}) => {
    try {
      // Track in Supabase
      await supabase.from('analytics_events').insert({
        event_type: eventType,
        page_path: location.pathname,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        session_id: getSessionId(),
        metadata
      });

      // Track in Google Analytics
      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('event', eventType, {
          page_path: location.pathname,
          custom_parameter_1: JSON.stringify(metadata)
        });
      }
    } catch (error) {
      console.error('Analytics tracking error:', error);
    }
  };

  // Track page views automatically
  useEffect(() => {
    trackEvent('page_view');
    
    // Also send page view to Google Analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('config', 'G-RGGQCHGB6H', {
        page_path: location.pathname,
        page_title: document.title
      });
    }
  }, [location.pathname]);

  // Set up scroll tracking
  useEffect(() => {
    let scrollThreshold = 0;
    const handleScroll = () => {
      const scrollPercent = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      
      if (scrollPercent >= 25 && scrollThreshold < 25) {
        scrollThreshold = 25;
        trackEvent('scroll', { percentage: 25 });
      } else if (scrollPercent >= 50 && scrollThreshold < 50) {
        scrollThreshold = 50;
        trackEvent('scroll', { percentage: 50 });
      } else if (scrollPercent >= 75 && scrollThreshold < 75) {
        scrollThreshold = 75;
        trackEvent('scroll', { percentage: 75 });
      } else if (scrollPercent >= 90 && scrollThreshold < 90) {
        scrollThreshold = 90;
        trackEvent('scroll', { percentage: 90 });
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  // Track form interactions
  const trackFormOpen = (formName: string) => {
    trackEvent('form_interaction', { action: 'open', formName });
  };

  const trackFormSubmit = (formName: string) => {
    trackEvent('form_interaction', { action: 'submit', formName });
  };

  // Track clicks
  const trackClick = (elementType: string, elementId?: string, elementText?: string) => {
    trackEvent('click', { 
      elementType, 
      elementId, 
      elementText: elementText?.substring(0, 100) // Limit text length
    });
  };

  // Track social media clicks
  const trackSocialClick = (platform: string, url: string) => {
    trackEvent('click', { 
      elementType: 'social_media', 
      platform, 
      url 
    });
  };

  return {
    trackEvent,
    trackFormOpen,
    trackFormSubmit,
    trackClick,
    trackSocialClick
  };
};

// Higher-order component to automatically track clicks
export const withAnalytics = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const { trackClick } = useAnalytics();
    
    const handleClick = (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const elementType = target.tagName.toLowerCase();
      const elementId = target.id;
      const elementText = target.textContent;
      
      trackClick(elementType, elementId, elementText);
      
      if (props.onClick) {
        props.onClick(e);
      }
    };

    return <Component {...props} onClick={handleClick} />;
  };
};